import React, { useState } from "react";
import {
  Button,
  Container,
  Form,
  Alert,
  Card,
} from "react-bootstrap";
import "../../styles/components/FileUploader.css";
import { callOperation } from "../../utils/apiHelper.js";
import { EmailField, PasswordField, VerificationCodeField, SubmitButton } from "../common/FormComponents";

const LoginPage = ({ onAuthenticate }) => {
  const [authMode, setAuthMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Reusable error handling function
  const handleError = (err, fallbackMessage) => {
    const errorMessage = err.response?.data?.error || err.message || fallbackMessage;
    setError(errorMessage);
    setIsLoading(false);
    return errorMessage;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Validate form
    if (!email || !password) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    try {
      const response = await callOperation("auth_signin", { email, password }, null, 'auth');

      const userData = response.data;
      onAuthenticate({
        email: userData.user.email,
        token: userData.access_token
      });
    } catch (err) {
      const errorMessage = handleError(err, "Sign in failed");

      // If email not verified, automatically switch to verification mode
      if (errorMessage.includes("Email not verified")) {
        setError(""); // Clear error since we're showing success
        setSuccess("Please verify your email first. Check your inbox for the verification code.");
        setAuthMode("verify");
      }
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Validate form
    if (!email || !password || !confirmPassword) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    // Comprehensive password validation
    const passwordRequirements = [];
    if (password.length < 8) passwordRequirements.push("at least 8 characters");
    if (!/[a-z]/.test(password)) passwordRequirements.push("lowercase letters");
    if (!/[A-Z]/.test(password)) passwordRequirements.push("uppercase letters");
    if (!/\d/.test(password)) passwordRequirements.push("numbers");
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)) passwordRequirements.push("symbols");

    if (passwordRequirements.length > 0) {
      setError(`Password must include: ${passwordRequirements.join(", ")}`);
      setIsLoading(false);
      return;
    }

    try {
      await callOperation("auth_signup", { email, password }, null, 'auth');

      setSuccess("Account created! Please check your email for verification code.");
      setAuthMode("verify");
      setIsLoading(false);
    } catch (err) {
      const errorMessage = handleError(err, "Sign up failed");

      // If account exists, provide helpful guidance
      if (errorMessage.includes("account with this email already exists")) {
        setError("Account already exists. Try signing in, or click 'Resend code' if you need to verify your email.");
      }
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a 6-digit verification code");
      setIsLoading(false);
      return;
    }

    try {
      await callOperation("auth_verify_email", { email, confirmation_code: verificationCode }, null, 'auth');

      setSuccess("Email verified! Signing you in...");

      // After verification, automatically sign in the user
      const signInResponse = await callOperation("auth_signin", { email, password }, null, 'auth');

      const userData = signInResponse.data;
      onAuthenticate({
        email: userData.user.email,
        token: userData.access_token
      });
    } catch (err) {
      handleError(err, "Verification failed");
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError("");
    try {
      await callOperation("auth_resend_code", { email }, null, 'auth');

      setSuccess("Verification code sent!");
      setIsLoading(false);
    } catch (err) {
      handleError(err, "Failed to resend code");
    }
  };

  const renderForm = () => {
    if (authMode === "signin") {
      return (
        <Form onSubmit={handleSignIn} className="slide-up">
          <EmailField
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <PasswordField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            showPassword={showPassword}
            onToggleShow={() => setShowPassword(!showPassword)}
          />
          <SubmitButton
            isLoading={isLoading}
            loadingText="Signing in..."
            defaultText="Sign In"
          />
          <div className="text-center">
            <Button
              variant="link"
              onClick={() => {
                setAuthMode("signup");
                setError("");
                setSuccess("");
              }}
              className="text-primary"
            >
              Don't have an account? Sign up
            </Button>
          </div>
        </Form>
      );
    }

    if (authMode === "signup") {
      return (
        <Form onSubmit={handleSignUp} className="slide-up">
          <EmailField
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <PasswordField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min 8 characters)"
            showPassword={showPassword}
            onToggleShow={() => setShowPassword(!showPassword)}
          />
          <PasswordField
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
            showPassword={showConfirmPassword}
            onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
          />
          <SubmitButton
            isLoading={isLoading}
            loadingText="Creating account..."
            defaultText="Sign Up"
          />
          <div className="text-center">
            <Button
              variant="link"
              onClick={() => {
                setAuthMode("signin");
                setError("");
                setSuccess("");
              }}
              className="text-primary"
            >
              Already have an account? Sign in
            </Button>
          </div>
        </Form>
      );
    }

    if (authMode === "verify") {
      return (
        <Form onSubmit={handleVerify} className="slide-up">
          <p className="text-muted mb-3">
            We sent a verification code to <strong>{email}</strong>
          </p>
          <VerificationCodeField
            value={verificationCode}
            onChange={setVerificationCode}
          />
          <SubmitButton
            isLoading={isLoading}
            loadingText="Verifying..."
            defaultText="Verify Email"
          />
          <div className="text-center">
            <Button
              variant="link"
              onClick={handleResendCode}
              disabled={isLoading}
              className="text-primary"
            >
              Resend code
            </Button>
            <br />
            <Button
              variant="link"
              onClick={() => {
                setAuthMode("signin");
                setError("");
                setSuccess("");
              }}
              className="text-secondary"
            >
              Back to sign in
            </Button>
          </div>
        </Form>
      );
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", padding: "2rem 1rem" }}>
      <div className="text-center" style={{ width: "100%", maxWidth: "400px" }}>
        <img
          src="/MPEG-G.png"
          alt="MPEG-G Logo"
          className="img-fluid mb-4 scale-in"
          style={{ maxWidth: "300px" }}
        />

        <Card className="shadow-sm bounce-in">
          <Card.Body className="p-4">
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" className="mb-3">
                {success}
              </Alert>
            )}
            {renderForm()}
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default LoginPage;
